import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import AppDropdown from '../../../components/AppDropdown'
import { AppTooltip, ChartTooltipSurface } from '../../../components/AppTooltip'
import {
  useGetTeamDashboardAvailabilityQuery,
  useGetTeamDashboardFiltersQuery,
  useGetTeamDashboardQuery,
  useGetTeamDashboardScheduleQuery,
  useGetTeamDashboardSlaQuery,
  useLazyGetTeamDashboardDrilldownQuery,
} from '../../../api/serverStateHooks'
import { AppSkeleton, WidgetSkeleton, ChartSkeleton, TableSkeleton } from '../../../components/skeleton'
import { resolveMediaUrl } from '../../../utils/mediaUrl'
import useBodyScrollLock from '../../../hooks/useBodyScrollLock'
import DashboardRightDrawer from '../shared/components/DashboardRightDrawer'

const RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 90 Days' },
]

const HEATMAP_WEEKDAY_COLUMNS = [
  { label: 'Sunday', backendIndex: 6 },
  { label: 'Monday', backendIndex: 0 },
  { label: 'Tuesday', backendIndex: 1 },
  { label: 'Wednesday', backendIndex: 2 },
  { label: 'Thursday', backendIndex: 3 },
  { label: 'Friday', backendIndex: 4 },
  { label: 'Saturday', backendIndex: 5 },
]
const TASK_HEATMAP_CELL_PX = 56
const RESOURCE_LOAD_CELL_PX = 40

function initialsOf(name) {
  return String(name || '')
    .split(' ')
    .map((word) => word.trim().charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

function availabilityTone(status) {
  const normalized = String(status || '').toUpperCase()
  if (normalized === 'ACTIVE') return { ring: 'ring-emerald-500', dot: 'bg-emerald-500' }
  if (normalized === 'IDLE') return { ring: 'ring-amber-500', dot: 'bg-amber-500' }
  if (normalized === 'BREAK') return { ring: 'ring-slate-500', dot: 'bg-slate-500' }
  return { ring: 'ring-slate-300', dot: 'bg-slate-300' }
}

function throughputCellClass(cell) {
  if (!cell?.inRange) return 'bg-blue-50'
  const value = Number(cell.value || 0)
  if (value > 110) return 'bg-blue-900'
  if (value >= 90) return 'bg-blue-700'
  if (value >= 70) return 'bg-blue-500'
  return 'bg-blue-300'
}

function qualityCellClass(cell) {
  if (!cell?.inRange) return 'bg-blue-50'
  const value = Number(cell.value || 0)
  if (value >= 95) return 'bg-blue-900'
  if (value >= 80) return 'bg-blue-700'
  if (value >= 60) return 'bg-blue-500'
  return 'bg-blue-300'
}

function heatCellTooltip(mode, cell) {
  if (!cell?.inRange) {
    return (
      <ChartTooltipSurface className="text-xs">
        <p className="font-semibold text-on-surface">Out of selected range</p>
      </ChartTooltipSurface>
    )
  }
  if (mode === 'throughput') {
    return (
      <ChartTooltipSurface className="text-xs">
        <p className="font-bold text-on-surface">{cell.date}</p>
        <p className="mt-1 text-on-surface-variant">Throughput: {Number(cell.value || 0).toFixed(1)}%</p>
        <p className="text-on-surface-variant">Logged: {Number(cell.outputHours || 0).toFixed(2)}h</p>
        <p className="text-on-surface-variant">Planned: {Number(cell.loadHours || 0).toFixed(2)}h</p>
      </ChartTooltipSurface>
    )
  }
  return (
    <ChartTooltipSurface className="text-xs">
      <p className="font-bold text-on-surface">{cell.date}</p>
      <p className="mt-1 text-on-surface-variant">Quality: {Number(cell.value || 0).toFixed(1)}%</p>
      <p className="text-on-surface-variant">On-time: {cell.onTimeCount || 0}</p>
      <p className="text-on-surface-variant">Completed: {cell.completedCount || 0}</p>
    </ChartTooltipSurface>
  )
}

function loadToneLabel(tone) {
  const normalized = String(tone || '').toLowerCase()
  if (normalized === 'critical') return 'Critical'
  if (normalized === 'high') return 'High'
  if (normalized === 'medium') return 'Medium'
  return 'Low'
}

function loadToneClass(tone) {
  const normalized = String(tone || '').toLowerCase()
  if (normalized === 'critical') return 'bg-red-500'
  if (normalized === 'high') return 'bg-amber-500'
  if (normalized === 'medium') return 'bg-emerald-500'
  return 'bg-emerald-200/80'
}

function loadCellTooltip(cell) {
  return (
    <ChartTooltipSurface className="text-xs">
      <p className="font-bold text-on-surface">{cell?.date || 'Unknown date'}</p>
      <p className="mt-1 text-on-surface-variant">Workload: {Number(cell?.value || 0).toFixed(1)}%</p>
      <p className="text-on-surface-variant">Band: {loadToneLabel(cell?.tone)}</p>
    </ChartTooltipSurface>
  )
}

function taskProductivityInfoTooltip() {
  return (
    <ChartTooltipSurface className="max-w-[300px] text-xs">
      <p className="font-bold text-on-surface">Task Productivity Logic</p>
      <p className="mt-1 text-on-surface-variant">Each square represents one day from the selected range.</p>
      <p className="text-on-surface-variant">Throughput = logged hours / planned hours.</p>
      <p className="text-on-surface-variant">Quality = on-time completions / completed jobs.</p>
      <p className="text-on-surface-variant">Darker shades indicate higher threshold intensity.</p>
    </ChartTooltipSurface>
  )
}

function resourceLoadInfoTooltip() {
  return (
    <ChartTooltipSurface className="max-w-[300px] text-xs">
      <p className="font-bold text-on-surface">Resource Load Logic</p>
      <p className="mt-1 text-on-surface-variant">Each square represents one day of team workload ratio.</p>
      <p className="text-on-surface-variant">Workload % = (planned hours active on date / team daily capacity) × 100.</p>
      <p className="text-on-surface-variant">Color is based on workload vs capacity thresholds.</p>
      <p className="text-on-surface-variant">Legend runs from low to critical load severity.</p>
    </ChartTooltipSurface>
  )
}

function formatMinutes(value) {
  const minutes = Number(value || 0)
  if (minutes <= 0) return '0m'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const rem = minutes % 60
  return `${hours}h ${rem}m`
}

function formatScheduleDate(isoString) {
  if (!isoString) return { month: '--', day: '--', time: '--' }
  const dt = new Date(isoString)
  return {
    month: dt.toLocaleDateString([], { month: 'short' }).toUpperCase(),
    day: String(dt.getDate()).padStart(2, '0'),
    time: dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  }
}

function normalizeHeatRows(cells, weeks) {
  const map = new Map()
  for (const cell of cells || []) {
    map.set(`${cell.weekKey}-${cell.weekdayIndex}`, cell)
  }
  return (weeks || []).map((week) => ({
    weekKey: week?.key || '',
    weekLabel: week?.label || '',
    cells: HEATMAP_WEEKDAY_COLUMNS.map((column) => map.get(`${week.key}-${column.backendIndex}`) || null),
  }))
}

export default function TeamDashboardScreen() {
  const navigate = useNavigate()
  const [teamId, setTeamId] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [rangeDays, setRangeDays] = useState('30')
  const [metricMode, setMetricMode] = useState('throughput')
  const [scheduleSidebarOpen, setScheduleSidebarOpen] = useState(false)
  const [schedulePage, setSchedulePage] = useState(1)
  const [slaSidebarOpen, setSlaSidebarOpen] = useState(false)
  const [slaPage, setSlaPage] = useState(1)
  const [drill, setDrill] = useState({ open: false, level: '', title: '', rows: [], columns: [], total: 0, page: 1, pageSize: 25, breadcrumb: [], dimensions: {}, loading: false, error: '', calculationBasis: '' })
  const pageSize = 10
  const [loadTeamDrill] = useLazyGetTeamDashboardDrilldownQuery()

  useBodyScrollLock(scheduleSidebarOpen || slaSidebarOpen || drill.open)

  const baseFiltersParams = useMemo(
    () => ({
      teamId: teamId || undefined,
    }),
    [teamId],
  )
  const { data: filtersData, isLoading: filtersLoading, isError: filtersError } = useGetTeamDashboardFiltersQuery(baseFiltersParams)
  const filtersReady = !filtersLoading && Boolean(filtersData)
  const noVisibleTeams = filtersReady && Array.isArray(filtersData?.teams) && filtersData.teams.length === 0
  const skipDependentQueries = !filtersReady || noVisibleTeams

  const teamOptions = useMemo(() => {
    const items = Array.isArray(filtersData?.teams) ? filtersData.teams : []
    return [
      { value: '', label: 'All Teams' },
      ...items.map((item) => ({ value: String(item.id), label: item.name })),
    ]
  }, [filtersData?.teams])

  const departments = useMemo(() => {
    const items = Array.isArray(filtersData?.departments) ? filtersData.departments : []
    return [{ value: '', label: 'All Departments' }, ...items.map((item) => ({ value: String(item.id), label: item.name }))]
  }, [filtersData?.departments])

  useEffect(() => {
    if (!departmentId) return
    const exists = departments.some((item) => item.value === String(departmentId))
    if (!exists) setDepartmentId('')
  }, [departmentId, departments])

  const dashboardParams = useMemo(
    () => ({
      teamId: teamId || undefined,
      departmentId: departmentId || undefined,
      days: Number(rangeDays || 30),
    }),
    [teamId, departmentId, rangeDays],
  )

  async function openDrill(level, dimensions = {}, page = 1) {
    const effectiveDimensions = Object.keys(dimensions).length ? dimensions : (drill.dimensions || {})
    setDrill((prev) => ({ ...prev, open: true, level, dimensions: effectiveDimensions, page, loading: true, error: '' }))
    try {
      const result = await loadTeamDrill({ ...dashboardParams, ...effectiveDimensions, level, page, pageSize: 25 }).unwrap()
      setDrill({ open: true, level: result.level || level, title: result.title || 'Team details', rows: Array.isArray(result.rows) ? result.rows : [], columns: Array.isArray(result.columns) ? result.columns : [], total: Number(result.total || 0), page: Number(result.page || page), pageSize: Number(result.pageSize || 25), breadcrumb: Array.isArray(result.breadcrumb) ? result.breadcrumb : [], dimensions: effectiveDimensions, loading: false, error: '', calculationBasis: result.calculationBasis || '' })
    } catch (error) {
      setDrill((prev) => ({ ...prev, open: true, loading: false, error: error?.data?.detail || 'Failed to load team details.' }))
    }
  }

  async function exportDrill(format) {
    const params = new URLSearchParams({ ...Object.fromEntries(Object.entries({ ...dashboardParams, ...drill.dimensions, level: drill.level, format }).filter(([, value]) => value !== undefined && value !== null && value !== '')) })
    const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000'}/dashboard/team/export?${params.toString()}`, { headers: { authorization: `Bearer ${localStorage.getItem('authToken') || ''}` } })
    if (!response.ok) throw new Error('Export failed')
    const disposition = response.headers.get('content-disposition') || ''
    const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || `team-drill.${format}`
    const url = URL.createObjectURL(await response.blob()); const anchor = document.createElement('a'); anchor.href = url; anchor.download = filename; document.body.appendChild(anchor); anchor.click(); anchor.remove(); URL.revokeObjectURL(url)
  }

  const {
    data: dashboardData,
    isLoading: dashboardLoading,
    isError: dashboardError,
  } = useGetTeamDashboardQuery(dashboardParams, { skip: skipDependentQueries })

  const { data: availabilityData, refetch: refetchAvailability } = useGetTeamDashboardAvailabilityQuery(
    {
      teamId: teamId || undefined,
      departmentId: departmentId || undefined,
    },
    { pollingInterval: skipDependentQueries ? 0 : 60000, skip: skipDependentQueries },
  )

  useEffect(() => {
    if (skipDependentQueries) return undefined
    const onWsEvent = () => refetchAvailability()
    const onFocus = () => refetchAvailability()
    window.addEventListener('chat-ws-event', onWsEvent)
    window.addEventListener('focus', onFocus)
    return () => {
      window.removeEventListener('chat-ws-event', onWsEvent)
      window.removeEventListener('focus', onFocus)
    }
  }, [skipDependentQueries, refetchAvailability])

  const scheduleParams = useMemo(
    () => ({
      teamId: teamId || undefined,
      departmentId: departmentId || undefined,
      days: Number(rangeDays || 30),
      page: schedulePage,
      pageSize,
    }),
    [teamId, departmentId, rangeDays, schedulePage],
  )
  const { data: scheduleData, isLoading: scheduleLoading } = useGetTeamDashboardScheduleQuery(scheduleParams, {
    skip: skipDependentQueries || !scheduleSidebarOpen,
  })

  const slaParams = useMemo(
    () => ({
      teamId: teamId || undefined,
      departmentId: departmentId || undefined,
      days: Number(rangeDays || 30),
      page: slaPage,
      pageSize,
    }),
    [teamId, departmentId, rangeDays, slaPage],
  )
  const { data: slaData, isLoading: slaLoading } = useGetTeamDashboardSlaQuery(slaParams, {
    skip: skipDependentQueries || !slaSidebarOpen,
  })

  const availabilitySnapshot = availabilityData?.availabilitySnapshot || { counts: {}, members: [] }
  const availabilityCounts = availabilitySnapshot?.counts || { active: 0, idle: 0, break: 0, offShift: 0 }
  const availabilityMembers = Array.isArray(availabilitySnapshot?.members) ? availabilitySnapshot.members : []
  const visibleMembers = availabilityMembers.filter((item) => item.status !== 'OFF_SHIFT').slice(0, 12)

  const slaAtRisk = dashboardData?.slaAtRisk || {}
  const slaItems = Array.isArray(slaAtRisk?.items) ? slaAtRisk.items : []

  const taskProductivity = dashboardData?.taskProductivityAnalysis || {}
  const weeks = Array.isArray(taskProductivity?.weeks) ? taskProductivity.weeks : []
  const throughputCells = Array.isArray(taskProductivity?.throughput?.cells) ? taskProductivity.throughput.cells : []
  const qualityCells = Array.isArray(taskProductivity?.quality?.cells) ? taskProductivity.quality.cells : []
  const heatRows = useMemo(
    () => normalizeHeatRows(metricMode === 'throughput' ? throughputCells : qualityCells, weeks),
    [metricMode, throughputCells, qualityCells, weeks],
  )

  const leaderboardRows = Array.isArray(dashboardData?.pointsLeaderboard) ? dashboardData.pointsLeaderboard : []
  const loadCells = Array.isArray(dashboardData?.resourceLoadDistribution?.cells) ? dashboardData.resourceLoadDistribution.cells : []
  const commsPulse = dashboardData?.commsPulse || {}
  const recentComms = Array.isArray(commsPulse?.recentActivity) ? commsPulse.recentActivity : []
  const teamSchedule = dashboardData?.teamSchedule || {}
  const nextScheduleItems = Array.isArray(teamSchedule?.items) ? teamSchedule.items : []

  const scheduleTotalPages = Math.max(1, Math.ceil(Number(scheduleData?.totalCount || 0) / pageSize))
  const slaTotalPages = Math.max(1, Math.ceil(Number(slaData?.totalCount || 0) / pageSize))

  if ((filtersLoading || (!skipDependentQueries && dashboardLoading)) && !dashboardData) {
    return (
      <div className="mx-auto w-full max-w-[1600px] pb-14 space-y-8 animate-fade-in">
        {/* Header Skeleton */}
        <section className="flex flex-col items-start justify-between gap-5 lg:flex-row lg:items-end">
          <div className="space-y-2">
            <AppSkeleton width="340px" height="48px" className="rounded-xl" />
            <AppSkeleton width="420px" height="20px" className="rounded-lg" />
          </div>
          <div className="flex gap-3">
            <AppSkeleton width="180px" height="42px" className="rounded-md" />
            <AppSkeleton width="180px" height="42px" className="rounded-md" />
            <AppSkeleton width="140px" height="42px" className="rounded-md" />
          </div>
        </section>

        {/* Availability Strip Skeleton */}
        <WidgetSkeleton lines={1} showHeader={false} className="min-h-[70px]" />

        {/* Main Grid Skeletons */}
        <section className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-8">
            <ChartSkeleton height="360px" title="Team Productivity & Heatmap" subtitle="Throughput and quality breakdown" />
          </div>
          <div className="col-span-12 lg:col-span-4">
            <TableSkeleton rows={5} cols={2} title="Points Leaderboard" />
          </div>
        </section>

        <section className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-6">
            <TableSkeleton rows={4} cols={3} title="Upcoming Team Schedule" />
          </div>
          <div className="col-span-12 lg:col-span-6">
            <TableSkeleton rows={4} cols={3} title="SLA Compliance & Task Deadlines" />
          </div>
        </section>
      </div>
    )
  }

  if (noVisibleTeams) {
    return (
      <div className="mx-auto w-full max-w-[1100px] pb-14">
        <section className="mb-8 flex flex-col items-start justify-between gap-5 lg:flex-row lg:items-end">
          <div>
            <h1 className="text-6xl font-extrabold tracking-tight text-on-surface">Team Intelligence</h1>
            <p className="mt-2 text-lg text-on-surface-variant">No project teams are visible for your current data scope.</p>
          </div>
        </section>

        <section className="rounded-[2rem] border border-dashed border-outline-variant/60 bg-surface-container-low p-10 text-center">
          <span className="material-symbols-outlined text-6xl text-primary">groups_off</span>
          <h2 className="mt-4 text-3xl font-black text-on-surface">No visible teams yet</h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-on-surface-variant">
            This dashboard only shows teams from projects available in your data scope. Once you are added to an
            active scoped project team, availability, schedule, SLA, and leaderboard data will appear here.
          </p>
        </section>
      </div>
    )
  }

  if (filtersError || dashboardError) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center p-20 text-error">
        <span className="material-symbols-outlined mb-2 text-5xl">error</span>
        <p className="font-bold">Failed to load team dashboard data.</p>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-[1600px] pb-14">
      <section className="mb-8 flex flex-col items-start justify-between gap-5 lg:flex-row lg:items-end">
        <div>
          <h1 className="text-6xl font-extrabold tracking-tight text-on-surface">Team Intelligence</h1>
          <p className="mt-2 text-lg text-on-surface-variant">
            Real-time oversight for {dashboardData?.team?.name || 'All Teams'}
          </p>
        </div>

        <div className="grid w-full gap-3 rounded-xl bg-surface-container-low p-3 md:w-auto md:grid-cols-3">
          <label className="grid min-w-[190px] gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-on-surface-variant">Team</span>
            <AppDropdown value={teamId} onChange={(value) => { setTeamId(String(value || '')); setDepartmentId(''); setSchedulePage(1); setSlaPage(1) }} options={teamOptions} />
          </label>
          <label className="grid min-w-[190px] gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-on-surface-variant">Department</span>
            <AppDropdown value={departmentId} onChange={(value) => { setDepartmentId(String(value || '')); setSchedulePage(1); setSlaPage(1) }} options={departments} />
          </label>
          <label className="grid min-w-[170px] gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-on-surface-variant">Date Range</span>
            <AppDropdown value={rangeDays} onChange={(value) => { setRangeDays(String(value || '30')); setSchedulePage(1); setSlaPage(1) }} options={RANGE_OPTIONS} />
          </label>
        </div>
      </section>

      <section className="grid grid-cols-12 gap-6">
        <article className="col-span-12 rounded-3xl bg-surface-container-low p-6 lg:col-span-8">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">sensors</span>
              <h2 className="text-3xl font-bold text-on-surface">Availability Snapshot</h2>
            </div>
            <div className="flex items-center gap-4 text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
              <button type="button" onClick={() => openDrill('availability', { status: 'ACTIVE' })} className="inline-flex items-center gap-1.5 hover:text-primary"><i className="h-2 w-2 rounded-full bg-emerald-500" /> Active ({availabilityCounts.active || 0})</button>
              <button type="button" onClick={() => openDrill('availability', { status: 'IDLE' })} className="inline-flex items-center gap-1.5 hover:text-primary"><i className="h-2 w-2 rounded-full bg-amber-500" /> Idle ({availabilityCounts.idle || 0})</button>
              <button type="button" onClick={() => openDrill('availability', { status: 'BREAK' })} className="inline-flex items-center gap-1.5 hover:text-primary"><i className="h-2 w-2 rounded-full bg-slate-500" /> Break ({availabilityCounts.break || 0})</button>
              <button type="button" onClick={() => openDrill('availability', { status: 'OFF_SHIFT' })} className="inline-flex items-center gap-1.5 hover:text-primary">Off-shift ({availabilityCounts.offShift || 0})</button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-8">
            {visibleMembers.map((member) => {
              const tone = availabilityTone(member.status)
              const avatarUrl = resolveMediaUrl(member.profilePhotoPath)
              return (
                <button type="button" key={member.userId} onClick={() => openDrill('availability', { userId: member.userId })} className="flex flex-col items-center gap-2 rounded-lg p-1 hover:bg-surface-container-high focus:outline-none focus:ring-2 focus:ring-primary">
                  <div className="relative">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={member.name} className={`h-12 w-12 rounded-full ring-2 ring-offset-2 ${tone.ring}`} />
                    ) : (
                      <div className={`flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary ring-2 ring-offset-2 ${tone.ring}`}>
                        {initialsOf(member.name)}
                      </div>
                    )}
                    <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white ${tone.dot}`} />
                  </div>
                  <span className="text-[11px] font-semibold text-on-surface">{member.name}</span>
                </button>
              )
            })}

            <div className="flex flex-col items-center gap-2">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-outline-variant/40 bg-surface-container-lowest text-xs font-bold text-on-surface">
                {availabilityCounts.offShift || 0}
              </div>
              <span className="text-[11px] font-semibold text-on-surface-variant">Off-shift</span>
            </div>
          </div>
        </article>

        <article className="col-span-12 rounded-3xl border-l-4 border-error bg-error-container/30 p-6 lg:col-span-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-error">
              <span className="material-symbols-outlined">warning</span>
              <h2 className="text-3xl font-bold">SLA At Risk</h2>
            </div>
            <span className="rounded bg-error px-2 py-1 text-[10px] font-bold text-white">
              {(slaAtRisk.criticalCount || 0)} CRITICAL
            </span>
          </div>

          <div className="space-y-3">
            {slaItems.slice(0, 3).map((item, index) => (
              <button type="button" key={`${item.entityType}-${item.entityId}-${index}`} onClick={() => openDrill('sla')} className="w-full rounded-xl border-l-2 border-error bg-surface-container-lowest p-3 text-left hover:bg-surface-container-low">
                <p className="text-sm font-bold text-on-surface">{item.taskName || item.jobName || item.taskCode || item.jobCode}</p>
                <p className="text-xs text-on-surface-variant">
                  {item.projectName || 'Project'} - {item.severity}
                </p>
                <p className="mt-1 text-[11px] font-black text-error">{item.remainingLabel || 'At risk'}</p>
              </button>
            ))}
            {(slaAtRisk.criticalCount || 0) > 3 ? (
              <button
                type="button"
                className="w-full rounded-xl bg-surface-container-lowest p-3 text-left text-[11px] font-semibold text-on-surface-variant"
                onClick={() => {
                  setSlaPage(1)
                  setSlaSidebarOpen(true)
                }}
              >
                View {(slaAtRisk.criticalCount || 0) - 3} other items...
              </button>
            ) : null}
          </div>
        </article>

        <article className="col-span-12 rounded-3xl border border-outline-variant/20 bg-surface-container-lowest p-6 lg:col-span-7">
          <div className="mb-8 flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-3xl font-bold text-on-surface">Task Productivity Analysis</h2>
                <AppTooltip content={taskProductivityInfoTooltip()} contentClassName="p-0 border-0 shadow-none bg-transparent">
                  <button type="button" className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-outline-variant/40 text-on-surface-variant">
                    <span className="material-symbols-outlined text-base">info</span>
                  </button>
                </AppTooltip>
              </div>
              <p className="text-sm text-on-surface-variant">Output vs Load Heatmap</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={`rounded-full px-3 py-1 text-xs font-bold ${metricMode === 'throughput' ? 'bg-primary text-on-primary' : 'bg-surface-container-high text-on-surface-variant'}`}
                onClick={() => setMetricMode('throughput')}
              >
                Throughput
              </button>
              <button
                type="button"
                className={`rounded-full px-3 py-1 text-xs font-bold ${metricMode === 'quality' ? 'bg-primary text-on-primary' : 'bg-surface-container-high text-on-surface-variant'}`}
                onClick={() => setMetricMode('quality')}
              >
                Quality
              </button>
            </div>
          </div>

          <div className="w-full overflow-x-auto">
            <div
              className="mb-2 grid gap-1.5 text-[10px] font-bold uppercase tracking-wide text-on-surface-variant"
              style={{ gridTemplateColumns: `repeat(7, ${TASK_HEATMAP_CELL_PX}px)` }}
            >
              {HEATMAP_WEEKDAY_COLUMNS.map((column) => (
                <span key={column.label} className="text-center text-[9px]">{column.label.slice(0, 3)}</span>
              ))}
            </div>

            <div className="space-y-1.5">
              {heatRows.map((row) => (
                <div
                  key={`${row.weekKey}-${row.weekLabel}`}
                  className="grid gap-1.5"
                  style={{ gridTemplateColumns: `repeat(7, ${TASK_HEATMAP_CELL_PX}px)` }}
                >
                  {row.cells.map((cell, index) => (
                    <AppTooltip key={`${row.weekKey}-${index}`} content={heatCellTooltip(metricMode, cell)} contentClassName="p-0 border-0 shadow-none bg-transparent">
                      <button
                        type="button"
                        className={`h-14 w-14 rounded-[4px] border border-outline-variant/20 transition-opacity hover:opacity-90 ${metricMode === 'throughput' ? throughputCellClass(cell) : qualityCellClass(cell)}`}
                        aria-label={
                          cell?.inRange
                            ? `${row.weekLabel || ''} ${cell?.date || ''} ${metricMode} ${Number(cell.value || 0).toFixed(1)} percent`
                            : `${row.weekLabel || 'Date row'} out of selected range`
                        }
                        onClick={() => cell?.inRange && openDrill('task_productivity', { fromDate: cell.date, toDate: cell.date, metric: metricMode })}
                      />
                    </AppTooltip>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
            <span>Low</span>
            <span className="inline-flex items-center gap-1">
              <span className="h-4 w-4 rounded-sm bg-blue-50" />
              <span className="h-4 w-4 rounded-sm bg-blue-300" />
              <span className="h-4 w-4 rounded-sm bg-blue-500" />
              <span className="h-4 w-4 rounded-sm bg-blue-900" />
            </span>
            <span>Critical</span>
          </div>
        </article>

        <article className="col-span-12 rounded-3xl bg-surface-container-low p-6 lg:col-span-5">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="text-3xl font-bold text-on-surface">Points Leaderboard</h2>
            <span className="material-symbols-outlined text-primary">military_tech</span>
          </div>

          <div className="space-y-4">
            {leaderboardRows.slice(0, 5).map((row) => {
              const avatarUrl = resolveMediaUrl(row.profilePhotoPath)
              return (
                <button type="button" key={row.userId} onClick={() => openDrill('points', { userId: row.userId })} className="flex w-full items-center gap-4 rounded-lg p-1 text-left hover:bg-surface-container-high focus:outline-none focus:ring-2 focus:ring-primary">
                  <span className="w-4 text-xl font-black text-primary/30">{row.rank}</span>
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={row.name} className="h-10 w-10 rounded-full border-2 border-white shadow-sm" />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-white bg-primary/10 text-[10px] font-bold text-primary shadow-sm">
                      {initialsOf(row.name)}
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-bold text-on-surface">{row.name}</p>
                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-surface-variant">
                      <span className="block h-full rounded-full bg-primary" style={{ width: `${Math.min(Number(row.progress || 0), 100)}%` }} />
                    </div>
                  </div>
                  <span className="text-xs font-black text-primary">{Number(row.periodPoints || 0).toLocaleString()}</span>
                </button>
              )
            })}
            {!leaderboardRows.length ? <p className="text-sm text-on-surface-variant">No leaderboard data for selected filters.</p> : null}
          </div>
        </article>

        <article className="col-span-12 rounded-3xl bg-surface-container-low p-6 lg:col-span-4">
          <div className="mb-6 flex items-center gap-2">
            <h2 className="text-3xl font-bold text-on-surface">Resource Load Distribution</h2>
            <AppTooltip content={resourceLoadInfoTooltip()} contentClassName="p-0 border-0 shadow-none bg-transparent">
              <button type="button" className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-outline-variant/40 text-on-surface-variant">
                <span className="material-symbols-outlined text-base">info</span>
              </button>
            </AppTooltip>
          </div>
          <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(7, ${RESOURCE_LOAD_CELL_PX}px)` }}>
            {loadCells.map((cell) => (
              <AppTooltip key={cell.date} content={loadCellTooltip(cell)} contentClassName="p-0 border-0 shadow-none bg-transparent">
                <button type="button" aria-label={`View ${loadToneLabel(cell.tone)} load details for ${cell.date}`} onClick={() => openDrill('resource_load', { fromDate: cell.date, toDate: cell.date, loadBand: cell.tone })} className={`h-10 w-10 rounded-sm ${loadToneClass(cell.tone)} focus:outline-none focus:ring-2 focus:ring-primary`} />
              </AppTooltip>
            ))}
            {!loadCells.length
              ? Array.from({ length: 21 }).map((_, idx) => <span key={`empty-${idx}`} className="h-10 w-10 rounded-sm bg-emerald-200" />)
              : null}
          </div>
          <div className="mt-4 flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
            <span>Low</span>
            <span className="inline-flex items-center gap-1">
              <span className="h-4 w-4 rounded-sm bg-emerald-200" />
              <span className="h-4 w-4 rounded-sm bg-emerald-500" />
              <span className="h-4 w-4 rounded-sm bg-amber-500" />
              <span className="h-4 w-4 rounded-sm bg-red-500" />
            </span>
            <span>Critical</span>
          </div>
        </article>

        <article className="col-span-12 rounded-3xl border border-outline-variant/20 bg-surface-container-lowest p-6 lg:col-span-4">
          <div className="mb-5 flex items-center gap-2">
            <span className="material-symbols-outlined text-primary">chat_bubble</span>
            <h2 className="text-3xl font-bold text-on-surface">Comms Pulse</h2>
          </div>

          <div className="space-y-4">
            <div className="flex gap-3">
              <span className="material-symbols-outlined rounded-lg bg-primary-fixed p-2 text-primary">forum</span>
              <div>
                <p className="text-sm font-bold text-on-surface">{Number(commsPulse.activeThreads || 0)} Active Threads</p>
                <p className="text-xs text-on-surface-variant">Average response time: {formatMinutes(commsPulse.avgReplyMinutes)}</p>
              </div>
            </div>

            <div className="rounded-xl bg-primary-fixed p-3">
              <p className="text-xs font-bold uppercase tracking-widest text-primary">Recent Activity</p>
              <div className="mt-2 space-y-2">
                {recentComms.slice(0, 3).map((row) => (
                  <button type="button" key={row.channelId} onClick={() => openDrill('communications', { channelId: row.channelId })} className="flex w-full items-center justify-between gap-2 rounded text-left text-xs hover:bg-primary-container/10">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-on-surface">{row.channelName}</p>
                      <p className="text-on-surface-variant">{row.lastMessageRelative || 'No activity'}</p>
                    </div>
                    <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-black text-white">{row.unreadCount || 0}</span>
                  </button>
                ))}
                {!recentComms.length ? <p className="text-xs text-on-surface-variant">No project-channel activity yet.</p> : null}
              </div>
            </div>
          </div>
        </article>

        <article className="col-span-12 rounded-3xl bg-surface-container-low p-6 lg:col-span-4">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-3xl font-bold text-on-surface">Team Schedule</h2>
            <button
              type="button"
              className="text-xs font-bold uppercase tracking-widest text-primary hover:underline"
              onClick={() => {
                setSchedulePage(1)
                setScheduleSidebarOpen(true)
              }}
            >
              Full View
            </button>
          </div>

          <div className="space-y-3">
            {nextScheduleItems.map((item, index) => {
              const formatted = formatScheduleDate(item.startTime)
              return (
                <button type="button" key={`${item.itemType}-${item.itemId}-${index}`} onClick={() => openDrill('schedule')} className="flex w-full gap-3 rounded-lg p-1 text-left hover:bg-surface-container-high">
                  <div className="flex h-10 w-10 flex-col items-center justify-center rounded-lg bg-surface-container-lowest shadow-sm">
                    <span className="text-[10px] font-bold uppercase text-on-surface-variant">{formatted.month}</span>
                    <span className="text-sm font-black text-primary">{formatted.day}</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-on-surface">{item.title}</p>
                    <p className="text-xs text-on-surface-variant">
                      {formatted.time}
                      {item.userName ? ` - ${item.userName}` : ''}
                    </p>
                  </div>
                </button>
              )
            })}
            {!nextScheduleItems.length ? <p className="text-sm text-on-surface-variant">No upcoming team schedules in selected range.</p> : null}
          </div>
        </article>
      </section>

      <DashboardRightDrawer
        open={scheduleSidebarOpen}
        title="Team Schedule Full View"
        subtitle={`${Number(scheduleData?.totalCount || 0)} filtered items`}
        onClose={() => setScheduleSidebarOpen(false)}
        widthClassName="max-w-[700px]"
        page={schedulePage}
        totalPages={scheduleTotalPages}
        onPrevious={() => setSchedulePage((prev) => Math.max(prev - 1, 1))}
        onNext={() => setSchedulePage((prev) => Math.min(scheduleTotalPages, prev + 1))}
        onPageChange={setSchedulePage}
      >
        {scheduleLoading ? (
          <p className="text-sm text-on-surface-variant">Loading schedules...</p>
        ) : (
          <div className="space-y-3">
            {(scheduleData?.items || []).map((item, index) => {
              const formatted = formatScheduleDate(item.startTime)
              return (
                <div key={`${item.itemType}-${item.itemId}-${index}`} className="rounded-xl border border-outline-variant/25 bg-surface-container-low p-3">
                  <p className="text-sm font-bold text-on-surface">{item.title}</p>
                  <p className="mt-1 text-xs text-on-surface-variant">
                    {formatted.month} {formatted.day}, {formatted.time}
                    {item.userName ? ` - ${item.userName}` : ''}
                  </p>
                  <p className="mt-1 text-[11px] font-semibold text-primary">{item.itemType}</p>
                </div>
              )
            })}
            {!scheduleData?.items?.length ? <p className="text-sm text-on-surface-variant">No upcoming items.</p> : null}
          </div>
        )}
      </DashboardRightDrawer>

      <DashboardRightDrawer
        open={slaSidebarOpen}
        title="SLA At Risk Full View"
        subtitle={`${Number(slaData?.totalCount || 0)} critical items`}
        onClose={() => setSlaSidebarOpen(false)}
        widthClassName="max-w-[700px]"
        page={slaPage}
        totalPages={slaTotalPages}
        onPrevious={() => setSlaPage((prev) => Math.max(prev - 1, 1))}
        onNext={() => setSlaPage((prev) => Math.min(slaTotalPages, prev + 1))}
        onPageChange={setSlaPage}
      >
        {slaLoading ? (
          <p className="text-sm text-on-surface-variant">Loading SLA items...</p>
        ) : (
          <div className="space-y-3">
            {(slaData?.items || []).map((item, index) => (
              <div key={`${item.entityType}-${item.entityId}-${index}`} className="rounded-xl border border-outline-variant/25 bg-surface-container-low p-3">
                <p className="text-sm font-bold text-on-surface">{item.taskName || item.jobName || item.taskCode || item.jobCode}</p>
                <p className="mt-1 text-xs text-on-surface-variant">{item.projectName || 'Project'} - {item.severity}</p>
                <p className="mt-1 text-[11px] font-semibold text-error">{item.remainingLabel || 'At risk'}</p>
              </div>
            ))}
            {!slaData?.items?.length ? <p className="text-sm text-on-surface-variant">No SLA items for selected filters.</p> : null}
          </div>
        )}
      </DashboardRightDrawer>

      <DashboardRightDrawer
        open={drill.open}
        title={drill.title || 'Team details'}
        subtitle={(drill.breadcrumb || []).join(' › ')}
        onClose={() => setDrill((prev) => ({ ...prev, open: false }))}
        widthClassName="max-w-[850px]"
        page={drill.page}
        totalPages={Math.max(1, Math.ceil(Number(drill.total || 0) / Math.max(1, Number(drill.pageSize || 25))))}
        onPrevious={() => openDrill(drill.level, drill.dimensions, Math.max(1, drill.page - 1))}
        onNext={() => openDrill(drill.level, drill.dimensions, drill.page + 1)}
        onPageChange={(page) => openDrill(drill.level, drill.dimensions, Number(page))}
        headerExtra={<div className="space-y-2"><div className="flex flex-wrap gap-2"><button type="button" onClick={() => setDrill((prev) => ({ ...prev, open: false }))} className="rounded-lg border border-outline-variant/30 px-3 py-1.5 text-xs font-bold text-primary">Back</button><button type="button" onClick={() => setDrill((prev) => ({ ...prev, open: false }))} className="rounded-lg border border-outline-variant/30 px-3 py-1.5 text-xs font-bold text-primary">Up one level</button><button type="button" onClick={() => setDrill((prev) => ({ ...prev, open: false, dimensions: {} }))} className="rounded-lg border border-outline-variant/30 px-3 py-1.5 text-xs font-bold text-primary">Reset drilldown</button><button type="button" onClick={() => exportDrill('csv')} className="rounded-lg border border-outline-variant/30 px-3 py-1.5 text-xs font-bold text-primary">CSV</button><button type="button" onClick={() => exportDrill('pdf')} className="rounded-lg border border-outline-variant/30 px-3 py-1.5 text-xs font-bold text-primary">PDF</button></div><p className="text-xs text-on-surface-variant">Current team, department and date filters are retained. {drill.calculationBasis}</p></div>}
      >
        {drill.loading ? <p className="py-10 text-center text-sm text-on-surface-variant">Loading details...</p> : null}
        {drill.error ? <p className="rounded-lg bg-error-container p-3 text-sm text-error">{drill.error}</p> : null}
        {!drill.loading && !drill.error && !drill.rows.length ? <p className="py-10 text-center text-sm text-on-surface-variant">No records match the selected criteria.</p> : null}
        {!drill.loading && !drill.error && drill.rows.length ? <div className="overflow-x-auto"><table className="min-w-full text-left text-xs"><thead className="bg-surface-container-low"><tr>{drill.columns.map((column) => <th key={column.key} className="whitespace-nowrap px-3 py-2 font-bold text-on-surface-variant">{column.label}</th>)}<th className="px-3 py-2 font-bold text-on-surface-variant">Source</th></tr></thead><tbody className="divide-y divide-outline-variant/15">{drill.rows.map((row, index) => { const path = row.timesheetId ? `/nerve/timesheet/entry?id=${row.timesheetId}&mode=view` : row.jobId ? `/nerve/job/entry?id=${row.jobId}` : row.taskId ? `/nerve/task/entry?id=${row.taskId}` : row.projectId ? `/nerve/project/detail?id=${row.projectId}` : null; return <tr key={row.timesheetId || row.pointEventId || row.messageId || row.itemId || row.sessionId || `${row.userId || 'row'}-${index}`}>{drill.columns.map((column) => <td key={column.key} className="whitespace-nowrap px-3 py-2 text-on-surface">{row[column.key] ?? '—'}</td>)}<td className="px-3 py-2">{path ? <button type="button" onClick={() => navigate(path, { state: { returnTo: window.location.pathname + window.location.search } })} className="font-bold text-primary hover:underline">View source</button> : <span className="text-on-surface-variant">In drawer</span>}</td></tr> })}</tbody></table></div> : null}
      </DashboardRightDrawer>

      {/* <button
        type="button"
        className="group fixed bottom-8 right-8 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-container text-on-primary shadow-2xl"
        aria-label="Refresh team dashboard"
        onClick={() => {
          refetchDashboard()
          refetchAvailability()
        }}
      >
        <span className="material-symbols-outlined text-3xl">refresh</span>
      </button> */}
    </div>
  )
}
